<template>
  <!-- Poster card — navigable via router-link -->
  <div v-if="variant === 'poster'" class="ui-card ui-card--poster" :class="{ 'ui-card--clickable': to || clickable }">
    <router-link v-if="to" :to="to" class="ui-card__link">
      <div class="ui-card__media-wrap">
        <slot name="media"></slot>
        <slot name="overlay"></slot>
      </div>
      <slot></slot>
      <slot name="footer"></slot>
    </router-link>
    <button v-else-if="clickable" type="button" class="ui-card__link ui-card__link--btn" @click="$emit('click', $event)">
      <div class="ui-card__media-wrap">
        <slot name="media"></slot>
        <slot name="overlay"></slot>
      </div>
      <slot></slot>
      <slot name="footer"></slot>
    </button>
    <template v-else>
      <div class="ui-card__media-wrap">
        <slot name="media"></slot>
        <slot name="overlay"></slot>
      </div>
      <slot></slot>
      <slot name="footer"></slot>
    </template>
  </div>

  <!-- Episode card -->
  <div v-else-if="variant === 'episode'" class="ui-card ui-card--episode" :class="{ 'ui-card--clickable': to || clickable }">
    <router-link v-if="to" :to="to" class="ui-card__link">
      <slot name="thumbnail"></slot>
      <slot></slot>
    </router-link>
    <button v-else-if="clickable" type="button" class="ui-card__link ui-card__link--btn" @click="$emit('click', $event)">
      <slot name="thumbnail"></slot>
      <slot></slot>
    </button>
    <template v-else>
      <slot name="thumbnail"></slot>
      <slot></slot>
    </template>
  </div>

  <!-- Hero card -->
  <div v-else-if="variant === 'hero'" class="ui-card ui-card--hero">
    <div class="ui-card__hero-media">
      <slot name="media"></slot>
      <div class="ui-card__hero-gradient-h" aria-hidden="true"></div>
      <div class="ui-card__hero-gradient-v" aria-hidden="true"></div>
    </div>
    <div class="ui-card__hero-content">
      <slot name="overlay"></slot>
      <slot></slot>
    </div>
    <slot name="footer"></slot>
  </div>
</template>

<script>
export default {
  name: 'UiCard',
  props: {
    variant: {
      type: String,
      default: 'poster',
      validator: v => ['poster', 'hero', 'episode'].includes(v),
    },
    to: { type: [String, Object], default: null },
    clickable: { type: Boolean, default: false },
  },
};
</script>

<style scoped lang="scss">
/* ── Poster card — width is controlled by .poster-card__inner in item_tpl ── */
.ui-card--poster {
  width: 100%;
}

.ui-card--clickable {
  cursor: pointer;
}

/* Link / button wrapper resets */
.ui-card__link {
  display: block;
  text-decoration: none;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  text-align: start;
  width: 100%;
}

.ui-card__link:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
  box-shadow: var(--shadow-glow);
  border-radius: var(--radius-sm);
}

.ui-card__media-wrap {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-sm);
}

.ui-card--poster .ui-card__media-wrap {
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
}

.ui-card--poster .ui-card__media-wrap :slotted(img),
.ui-card--poster .ui-card__media-wrap > :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Hover scale is handled by .poster-card in item_tpl.vue */

/* ── Hero card ── */
.ui-card--hero {
  position: relative;
  height: 360px;
  border-radius: var(--radius-md);
  overflow: hidden;
}

.ui-card__hero-media {
  position: absolute;
  inset: 0;
}

.ui-card__hero-media :slotted(img),
.ui-card__hero-media > :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* LTR gradient — dark on inline-start side */
.ui-card__hero-gradient-h {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 50%, transparent 100%);
}

/* RTL flips the horizontal gradient so dark side is behind text */
[dir="rtl"] .ui-card__hero-gradient-h {
  background: linear-gradient(-90deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 50%, transparent 100%);
}

.ui-card__hero-gradient-v {
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, rgba(0,0,0,0.6) 0%, transparent 50%);
}

.ui-card__hero-content {
  position: absolute;
  inset-inline-start: 40px;
  bottom: 40px;
  inset-inline-end: 40px;
  max-width: 540px;
}

/* ── Episode card ── */
.ui-card--episode {
  background: var(--color-surface1);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color var(--duration-slow) var(--easing-default),
              box-shadow var(--duration-slow) var(--easing-default);
}

.ui-card--episode .ui-card__link {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: var(--space-4);
  padding: var(--space-4);
}

.ui-card--episode:hover {
  border-color: var(--color-borderHi);
  box-shadow: var(--shadow-sm);
}

.ui-card--episode .ui-card__link:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
  box-shadow: var(--shadow-glow);
  border-radius: var(--radius-md);
}

@media (prefers-reduced-motion: reduce) {
  .ui-card--episode {
    transition: none;
  }
}
</style>
